import { useRef, useState } from "react";

import { chartTheme } from "./chartTheme";

/**
 * KpiTrend (UX-006 §5.16) — the daily KPI trend behind a tile's caret.
 *
 * One engine-computed figure per completed day, drawn as a line over the day axis.
 * The whole point of this component is the gap: `/v1/kpi/series` returns a WITHHELD
 * point for a day the engine produced no figure for, and a withheld day is not zero.
 * So the line is drawn as one segment per contiguous run of real values and simply
 * STOPS at a withheld day — never dropped to the floor, never interpolated across,
 * and never removed from the axis (which would make the axis lie about the gap).
 * Each withheld day is ticked under the baseline so the hole is visible as a hole.
 *
 * Nothing is computed here but pixel geometry. The y range is read off the values the
 * server sent; no rolling mean, no smoothing, no fitted slope (CLAUDE.md rule 2 — the
 * KPI engine owns every formula, and UX-006 §5.16: "No slope is fitted in the browser").
 */

/** A line's role in the grammar (UX-006 §2/§4) — never a raw colour at the call site. */
export type TrendLineKind = "measured" | "peer" | "reference" | "cumulative";

export interface TrendLine {
  /** Named in the caption and the legend, so a reader knows which line is which. */
  label: string;
  kind: TrendLineKind;
  /** One entry per day, aligned with `days`. `null` is a withheld day: the line breaks. */
  values: (number | null)[];
}

export interface KpiTrendProps {
  /** The y-axis unit, straight from the server's own field — never a literal. */
  unit: string;
  /** The x positions, in order, gaps included. Days (YYYY-MM-DD) by default; with
   *  `formatX` they may be any ordered key the caller can label, which is how the
   *  same chart serves an intraday window. */
  days: string[];
  /** How an x value is labelled. Omitted, it is read as a site-local ISO day.
   *  The chart does no timezone work of its own either way. */
  formatX?: (value: string) => string;
  lines: TrendLine[];
  height?: number;
  ariaLabel: string;
  /** The sr-only caption. The caller states the withheld count in words. */
  summary?: string;
}

/** The index runs of real values. A withheld day closes the current run. */
export function valueRuns(values: readonly (number | null)[]): number[][] {
  const runs: number[][] = [];
  let run: number[] = [];
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (typeof v === "number" && Number.isFinite(v)) {
      run.push(i);
    } else if (run.length > 0) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length > 0) runs.push(run);
  return runs;
}

/** Format an ISO day without crossing a timezone (the server's day is site-local). */
function dayLabel(iso?: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

const axisNum = (v: number): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(v);

export function KpiTrend({ unit, days, lines, height = 140, ariaLabel, summary,
                          formatX }: KpiTrendProps) {
  const labelX = formatX ?? dayLabel;
  // The readout under the pointer. A chart of a hundred days cannot label every
  // point, so the reader scrubs and the exact position and figure are printed in
  // words above the plot — as TEXT, outside the svg, which is drawn with
  // preserveAspectRatio="none" and would stretch any glyph inside it.
  const [at, setAt] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const theme = chartTheme();
  const W = 1000;
  const padL = 46, padR = 10, padT = 14, padB = 22;
  const n = days.length;
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;

  const finite = lines.flatMap((l) =>
    l.values.filter((v): v is number => typeof v === "number" && Number.isFinite(v)),
  );
  const lo = finite.length > 0 ? Math.min(...finite) : 0;
  const hi = finite.length > 0 ? Math.max(...finite) : 1;
  // A flat series still needs a band to sit in; pad by the magnitude, not by zero.
  const span = hi - lo || Math.abs(hi) || 1;
  // Padding below the lowest value put a NEGATIVE floor under series that cannot
  // go there — an axis reading -438 kW under a power curve, or -8% under an
  // availability. A series that never goes negative gets a floor of zero.
  const padded = lo - span * 0.08;
  const yMin = lo >= 0 ? Math.min(lo, Math.max(0, padded)) : padded;
  const yMax = hi + span * 0.08;

  const xOf = (i: number) => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yOf = (v: number) => padT + plotH - ((v - yMin) / (yMax - yMin)) * plotH;

  // Horizontal gridlines only (UX-006 §2): no vertical grid anywhere on the Platform.
  const gridValues = [yMin, (yMin + yMax) / 2, yMax];

  // Day labels thinned to roughly six so they never collide; first and last always shown.
  const labelEvery = Math.max(1, Math.ceil(n / 6));

  const strokeFor = (kind: TrendLineKind): React.SVGProps<SVGPolylineElement> => {
    switch (kind) {
      case "measured":
        return { stroke: theme.measured, strokeWidth: 2 };
      case "reference":
        return { stroke: theme.reference, strokeWidth: 1.5, strokeDasharray: "4 3" };
      // The cumulative is the one measured line in the grammar that is dashed (§5.16).
      case "cumulative":
        return { stroke: theme.measured, strokeWidth: 1.5, strokeDasharray: "6 3" };
      default:
        return { stroke: theme.peer, strokeWidth: 1 };
    }
  };

  const withheldDays = days
    .map((day, i) => ({ day, i }))
    .filter(({ i }) => lines.every((l) => {
      const v = l.values[i];
      return !(typeof v === "number" && Number.isFinite(v));
    }));

  // Pointer x -> the nearest position. The viewBox is 1000 wide and the aspect
  // ratio is free, so the mapping is a straight proportion of the rendered width.
  const track = (clientX: number): void => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || box.width === 0 || n === 0) return;
    const vb = ((clientX - box.left) / box.width) * W;
    const i = n <= 1 ? 0 : Math.round(((vb - padL) / plotW) * (n - 1));
    // An event that carries no usable coordinate must leave the readout alone
    // rather than print a blank position against a real-looking chart.
    if (!Number.isFinite(i)) return;
    setAt(Math.min(n - 1, Math.max(0, i)));
  };

  // The readout rides the crosshair rather than sitting in a corner: at a glance
  // the figure and the point it belongs to are the same place on the page. It is
  // positioned in PERCENT of the plot's own width, which is how the svg scales.
  const firstDrawn = at === null
    ? undefined
    : lines.map((l) => l.values[at as number]).find((v) => typeof v === "number");
  const readout = at === null ? null : (
    <div
      className="sk-chart-readout"
      style={{
        position: "absolute",
        left: `${(xOf(at) / W) * 100}%`,
        // Just above the point it names, or above the plot when the day is withheld.
        // Never above the figure's own top edge: the label is drawn UPWARD from
        // this line, so it needs its own height of room.
        top: Math.max(22, typeof firstDrawn === "number" ? yOf(firstDrawn) - 8 : padT),
        // Against the near edge the label turns inward instead of running off.
        transform: `translate(${
          at <= (n - 1) * 0.12 ? "0%" : at >= (n - 1) * 0.88 ? "-100%" : "-50%"
        }, -100%)`,
        display: "flex", alignItems: "baseline", gap: "0 8px",
        padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap", pointerEvents: "none",
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        fontFamily: "var(--sk-font-mono)", fontSize: 10, lineHeight: 1.5,
      }}
    >
      <b>{labelX(days[at] as string)}</b>
      {lines.map((l) => {
        const v = l.values[at];
        return (
          <span key={l.label} style={{ color: strokeFor(l.kind).stroke as string }}>
            {lines.length > 1 ? `${l.label} ` : ""}
            {typeof v === "number" ? `${axisNum(v)}${unit ? ` ${unit}` : ""}` : "no figure"}
          </span>
        );
      })}
    </div>
  );

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0"
            style={{ margin: 0, position: "relative" }}>
      <svg
        ref={svgRef}
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, touchAction: "pan-y" }}
        onPointerMove={(e) => track(e.clientX)}
        onPointerDown={(e) => track(e.clientX)}
        onPointerLeave={() => setAt(null)}
      >
        {gridValues.map((g, gi) => (
          <g key={gi}>
            <line x1={padL} y1={yOf(g)} x2={W - padR} y2={yOf(g)} stroke={theme.grid} strokeWidth={1} />
            <text
              x={padL - 5}
              y={yOf(g) + 3}
              textAnchor="end"
              fontSize={9}
              fill={theme.textMuted}
              fontFamily="var(--sk-font-mono)"
            >
              {axisNum(g)}
            </text>
          </g>
        ))}

        {/* The unit labels the axis, taken from the server's field (UX-006 §2/§6). */}
        {unit ? (
          <text x={2} y={9} fontSize={9} fontWeight={600} fill={theme.textMuted} data-unit={unit}>
            {unit}
          </text>
        ) : null}

        <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke={theme.axis} strokeWidth={1} />

        {days.map((day, i) =>
          i % labelEvery === 0 || i === n - 1 ? (
            <text
              key={day}
              x={xOf(i)}
              y={height - 6}
              textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
              fontSize={9}
              fill={theme.textMuted}
              fontFamily="var(--sk-font-mono)"
            >
              {labelX(day)}
            </text>
          ) : null,
        )}

        {/* A withheld day keeps its place on the axis and is ticked, so the gap in the
            record is visible rather than silently closed up. */}
        {withheldDays.map(({ day, i }) => (
          <line
            key={`withheld-${day}`}
            data-withheld-day={day}
            x1={xOf(i)}
            y1={padT + plotH + 1}
            x2={xOf(i)}
            y2={padT + plotH + 4}
            stroke={theme.marker}
            strokeWidth={1}
          />
        ))}

        {lines.map((line, li) => (
          <g key={line.label} data-series={li} data-kind={line.kind}>
            {valueRuns(line.values).map((run) =>
              run.length === 1 ? (
                // A single real day between two gaps is a dot: a one-point polyline
                // draws nothing, and the day did carry a figure.
                <circle
                  key={run[0]}
                  data-run={run[0]}
                  cx={xOf(run[0] as number)}
                  cy={yOf(line.values[run[0] as number] as number)}
                  r={2}
                  fill={strokeFor(line.kind).stroke as string}
                />
              ) : (
                <polyline
                  key={run[0]}
                  data-run={run[0]}
                  fill="none"
                  {...strokeFor(line.kind)}
                  points={run.map((i) => `${xOf(i)},${yOf(line.values[i] as number)}`).join(" ")}
                />
              ),
            )}
          </g>
        ))}
        {at !== null ? (
          <g data-crosshair={days[at]} pointerEvents="none">
            <line x1={xOf(at)} y1={padT} x2={xOf(at)} y2={padT + plotH}
                  stroke={theme.axis} strokeWidth={1} />
            {lines.map((l) => {
              const v = l.values[at];
              return typeof v === "number" ? (
                <circle key={l.label} cx={xOf(at)} cy={yOf(v)} r={2.5}
                        fill={strokeFor(l.kind).stroke as string} />
              ) : null;
            })}
          </g>
        ) : null}
      </svg>
      {readout}
      {summary ? <figcaption className="sr-only">{summary}</figcaption> : null}
    </figure>
  );
}
